import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Sparkles, Heart, Star } from 'lucide-react';

/**
 * Full-screen celebration overlay shown when the user clicks the Buy button.
 * Shows sparkles/particles, a big check mark, a personalized message,
 * then calls onComplete after ~2s.
 */
export default function BuyCelebration({ open, onComplete, price = 39 }) {
  const [phase, setPhase] = useState('burst'); // burst -> check -> message

  const particles = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 26; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 180 + Math.random() * 240;
      arr.push({
        id: i,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        rot: Math.random() * 720 - 360,
        delay: Math.random() * 0.15,
        size: 10 + Math.random() * 14,
        kind: i % 3,
        color: ['#34d399', '#fbbf24', '#f472b6', '#60a5fa'][i % 4],
      });
    }
    return arr;
  }, []);

  useEffect(() => {
    if (!open) return;
    setPhase('burst');
    const t1 = setTimeout(() => setPhase('check'), 400);
    const t2 = setTimeout(() => setPhase('message'), 900);
    const t3 = setTimeout(() => onComplete?.(), 2200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [open, onComplete]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 backdrop-blur-md">
      <style>{`
        @keyframes bc-particle {
          0% { transform: translate(0,0) rotate(0deg) scale(0.4); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(1); opacity: 0; }
        }
        @keyframes bc-ring {
          0% { transform: scale(0.4); opacity: 0.9; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes bc-check {
          0% { transform: scale(0) rotate(-30deg); opacity: 0; }
          60% { transform: scale(1.2) rotate(6deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes bc-slide-up {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .bc-particle { animation: bc-particle 1.4s cubic-bezier(.16,1.36,.5,1) forwards; }
        .bc-ring { animation: bc-ring 1s ease-out forwards; }
        .bc-check { animation: bc-check .55s cubic-bezier(.16,1.36,.5,1) forwards; }
        .bc-slide-up { animation: bc-slide-up .5s cubic-bezier(.2,.9,.3,1) forwards; }
      `}</style>

      <div className="relative flex flex-col items-center text-center px-6">
        {/* Expanding rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="bc-ring absolute w-40 h-40 rounded-full border-2 border-emerald-300/60" />
          <span
            className="bc-ring absolute w-40 h-40 rounded-full border-2 border-amber-300/60"
            style={{ animationDelay: '0.2s' }}
          />
        </div>

        {/* Particle burst */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {particles.map((p) => {
            const Icon = p.kind === 0 ? Sparkles : p.kind === 1 ? Star : Heart;
            return (
              <span
                key={p.id}
                className="bc-particle absolute"
                style={{
                  '--dx': `${p.dx}px`,
                  '--dy': `${p.dy}px`,
                  '--rot': `${p.rot}deg`,
                  animationDelay: `${p.delay}s`,
                  color: p.color,
                }}
              >
                <Icon size={p.size} fill={p.color} strokeWidth={0} />
              </span>
            );
          })}
        </div>

        {/* Big check */}
        <div className="relative">
          <div className="bc-check w-28 h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-br from-emerald-300 to-emerald-500 flex items-center justify-center shadow-[0_0_80px_-5px_rgba(16,185,129,0.8)]">
            <CheckCircle2 className="text-slate-900" size={72} strokeWidth={2.5} />
          </div>
        </div>

        {/* Message */}
        {phase === 'message' && (
          <div className="relative mt-8 max-w-md">
            <div className="bc-slide-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-xs font-bold uppercase tracking-widest">
                <Sparkles size={12} /> You&apos;re in!
              </div>
              <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                Welcome, master.
              </h2>
              <p className="mt-3 text-slate-300 leading-relaxed">
                Your ${Number(price).toLocaleString('en-US')} USD unlocks a lifetime of calm, focus and clarity.
                Preparing your instant lifetime access now&hellip;
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-emerald-300 text-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Redirecting you to your library
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
